'use client';

import geoContract from '@bells/api/geo/frontend';
import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import type { Locale } from '@bells/i18n/runtime';
import {
  FoundLocationType,
  type FoundLocation,
} from '@bells/schema/models/mapbox';
import { Command as CommandPrimitive } from 'cmdk';
import { isEmpty, orderBy } from 'lodash';
import { CircleX, House, Loader2, MapPin } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { toast } from '../../../utils/use-toast';
import { Button } from '../../../components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '../../../components/command';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/dialog';
import { Label } from '../../../components/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/tooltip';

import { useAppSettings } from '@bells/core';
import {
  crudNotFound,
  locationSearchMyAddress,
  locationSearchPlaceholder,
  locationSearchSuggestion,
  locationSearchWhereDesktop,
} from '@bells/i18n';
import { useRouter } from 'next/navigation';
import { useMediaQuery } from '../../../hooks/use-media-query';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../../../components/drawer';
import { FlagIcon } from '../../../components/flag-icon';
import { Alert, AlertTitle } from '../../../components/alert';

const Results = ({
  language,
  onSelect,
  term,
}: {
  language: Locale;
  onSelect: (val: FoundLocation) => void;
  term?: string;
}) => {
  const [searchInput, setSearchInput] = useState<string>(term ?? '');
  const [debouncedSearchInput] = useDebounce(searchInput, 150);

  useEffect(() => {
    setSearchInput(term ?? '');
  }, [term]);

  const { mutate, isPending, data, error, isError } =
    systemRestClient.findLocation.useMutation();

  useEffect(() => {
    mutate({ body: { term: debouncedSearchInput, language: language } });
  }, [debouncedSearchInput]);

  const sortedData = useMemo(() => {
    if (!data) return [];

    const filtered = (data.body.items ?? []).filter((item) => item?.name);
    const searchLower = debouncedSearchInput.toLowerCase();

    if (!searchLower) {
      const countryPriority = ['de', 'at', 'ct'];
      return orderBy(
        filtered,
        [
          (item) => countryPriority.indexOf(item.country) + 1 || 99,
          (item) => item.shortName.toLowerCase(),
        ],
        ['asc', 'asc']
      );
    }

    return orderBy(
      filtered,
      [
        (item) => (item.shortName.toLowerCase() === searchLower ? 0 : 1),
        (item) =>
          item.shortName.toLowerCase().startsWith(searchLower) ? 0 : 1,
        (item) => (item.shortName.toLowerCase().includes(searchLower) ? 0 : 1),
      ],
      ['asc', 'asc', 'asc']
    );
  }, [data, debouncedSearchInput]);

  return (
    <Command
      shouldFilter={false}
      className='m-0 h-full w-full flex-1 bg-transparent'
    >
      <CommandInput
        value={searchInput}
        className='text-base font-medium'
        onValueChange={(val) => setSearchInput(val)}
        placeholder={locationSearchPlaceholder()}
      />
      {isError && (error as any)?.body?.message && (
        <Alert variant={'destructive'} className='mt-4'>
          <CircleX className='h-4 w-4' />
          <AlertTitle>{(error as any)?.body?.message}</AlertTitle>
        </Alert>
      )}

      <CommandList className='h-full max-h-max bg-transparent'>
        <CommandEmpty>
          {!isPending && sortedData?.length === 0 && (
            <div className='flex items-center justify-center py-4'>
              {crudNotFound()}
            </div>
          )}
        </CommandEmpty>
        <CommandGroup className={cn('relative h-auto', { hidden: !isPending })}>
          <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        </CommandGroup>
        {sortedData && !isPending && sortedData?.length > 0 && (
          <CommandGroup
            heading={
              isEmpty(debouncedSearchInput)
                ? locationSearchSuggestion()
                : undefined
            }
          >
            {sortedData.map((item, index) => {
              return (
                <CommandPrimitive.Item
                  value={`${item.name}-${item.lng}-${item.lat}`}
                  onSelect={() => {
                    onSelect(item);
                  }}
                  className={cn(
                    'my-2 flex h-max cursor-pointer select-text items-center gap-2 rounded-lg border p-2 px-3 ring-primary ring-offset-2 transition duration-150 last:my-0 hover:bg-muted data-[active="true"]:border-input data-[active="true"]:ring'
                  )}
                  key={`suggestion-${index}`}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className='flex w-full flex-col items-center gap-1'>
                    <div className='flex w-full justify-between gap-2'>
                      <div className='flex w-full flex-1 items-center gap-2 text-sm font-medium'>
                        <FlagIcon
                          countryCode={item.country.toUpperCase()}
                          className='h-4 w-4'
                        ></FlagIcon>
                        {item.name}
                      </div>
                      {item.type == FoundLocationType.hotel ? (
                        <House className='h-4 w-4 text-muted-foreground'></House>
                      ) : (
                        <MapPin className='h-4 w-4 text-muted-foreground'></MapPin>
                      )}
                    </div>
                    {item.address && (
                      <span className='w-full text-xs opacity-70'>
                        {item.address}
                      </span>
                    )}
                  </div>
                </CommandPrimitive.Item>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
};

const systemRestClient = generateRestClient(geoContract);

export type LocationSearchProps = {
  selected?: {
    name: string;
    countryCode: string;
    lng: number;
    lat: number;
    type: 'address' | 'hotel' | 'country';
  };
};

export type LocationSearchChangeEvent = {
  location?: FoundLocation;
};

type LocationSearchInternalProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onSelect' | 'onDurationChange'
> & {
  withSubmit?: boolean;
  duration?: number;
  onSubmit?: () => void;
  location?: FoundLocation;
  language: Locale;
  onSelect: (val: FoundLocation) => void;
  readLocationFromBrowser?: () => void;
};

const LocationSearchDesktop = React.forwardRef<
  HTMLDivElement,
  LocationSearchInternalProps
>(
  (
    {
      location,
      language,
      onSelect,

      readLocationFromBrowser,
      className,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return (
      <div
        ref={ref}
        className={cn(
          'relative grid w-full grid-cols-1 flex-wrap items-center gap-4 self-stretch rounded-xl border bg-background px-2 shadow-[0_8px_24px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)]',
          className
        )}
      >
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <div
              className={cn(
                'relative flex h-full flex-1 items-center justify-between gap-1 border-b bg-transparent px-2 py-4 sm:border-b-0'
              )}
            >
              <div className='flex flex-1 flex-col flex-nowrap gap-1'>
                <Label
                  htmlFor='dateWhere'
                  className='flex items-center gap-2 px-2 pt-2 text-sm font-semibold uppercase text-muted-foreground'
                >
                  {!location && (
                    <span className='relative flex size-3'>
                      <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                      <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
                    </span>
                  )}
                  {locationSearchWhereDesktop()}
                </Label>
                <div className='flex w-full items-center justify-between'>
                  <div className='w-full flex-1'>
                    <Button
                      variant={'ghost'}
                      className={cn(
                        'relative h-auto w-full max-w-full items-center justify-start whitespace-normal border-0 bg-transparent px-2 text-left text-base text-foreground',
                        {
                          'text-muted-foreground': !location?.name,
                        }
                      )}
                    >
                      <span className={cn('line-clamp-1 break-all')}>
                        {location?.name ?? locationSearchPlaceholder()}
                      </span>
                    </Button>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={(ev) => {
                          ev.preventDefault();
                          readLocationFromBrowser?.();
                        }}
                        size={'icon'}
                        variant={'ghost'}
                      >
                        <MapPin className='h-6 w-6'></MapPin>
                      </Button>
                    </TooltipTrigger>

                    <TooltipContent>{locationSearchMyAddress()}</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className='flex h-full w-full flex-col sm:max-h-[500px] sm:max-w-[500px] lg:max-h-[700px] lg:max-w-[700px]'>
            <DialogHeader>
              <DialogTitle>{locationSearchWhereDesktop()}</DialogTitle>
            </DialogHeader>
            <Results
              onSelect={(val) => {
                setIsOpen(false);
                onSelect(val);
              }}
              term={location?.name}
              language={language}
            ></Results>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
);

LocationSearchDesktop.displayName = 'LocationSearchDesktop';

const LocationSearchMobile = React.forwardRef<
  HTMLDivElement,
  LocationSearchInternalProps
>(
  (
    {
      location,
      language,
      onSelect,

      readLocationFromBrowser,
      className,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return (
      <div
        ref={ref}
        className={cn(
          'relative grid w-full grid-cols-1 flex-wrap items-center gap-4 self-stretch rounded-xl border bg-background px-2 shadow-[0_8px_24px_rgba(0,0,0,0.1)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.4)]',
          className
        )}
      >
        <Drawer open={isOpen} onOpenChange={setIsOpen}>
          <DrawerTrigger asChild>
            <div
              className={cn(
                'relative flex h-full flex-1 items-center justify-between gap-1 border-b bg-transparent px-2 py-4 sm:border-b-0'
              )}
            >
              <div className='flex flex-1 flex-col flex-nowrap gap-1'>
                <Label
                  htmlFor='dateWhere'
                  className='flex items-center gap-2 px-2 pt-2 text-sm font-semibold uppercase text-muted-foreground'
                >
                  {!location && (
                    <span className='relative flex size-3'>
                      <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-75'></span>
                      <span className='relative inline-flex size-3 rounded-full bg-pink-500'></span>
                    </span>
                  )}
                  {locationSearchWhereDesktop()}
                </Label>
                <div className='flex w-full items-center justify-between'>
                  <div className='w-full flex-1'>
                    <Button
                      variant={'ghost'}
                      className={cn(
                        'relative h-auto w-full max-w-full items-center justify-start whitespace-normal border-0 bg-transparent px-2 text-left text-base text-foreground',
                        {
                          'text-muted-foreground': !location?.name,
                        }
                      )}
                    >
                      <span className={cn('line-clamp-1 break-all')}>
                        {location?.name ?? locationSearchPlaceholder()}
                      </span>
                    </Button>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={(ev) => {
                          ev.preventDefault();
                          readLocationFromBrowser?.();
                        }}
                        size={'icon'}
                        variant={'ghost'}
                      >
                        <MapPin className='h-6 w-6'></MapPin>
                      </Button>
                    </TooltipTrigger>

                    <TooltipContent>{locationSearchMyAddress()}</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
          </DrawerTrigger>
          <DrawerContent className='mx-auto flex h-full max-h-[90vh] w-full max-w-screen-sm flex-col'>
            <DrawerHeader>
              <DrawerTitle>{locationSearchWhereDesktop()}</DrawerTitle>
            </DrawerHeader>
            <Results
              onSelect={(val) => {
                onSelect(val);
                setIsOpen(false);
              }}
              term={location?.name}
              language={language}
            ></Results>
          </DrawerContent>
        </Drawer>
      </div>
    );
  }
);

LocationSearchMobile.displayName = 'LocationSearchMobile';

const LocationSearchHero = React.forwardRef<
  HTMLDivElement,
  Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
    initLocation?: FoundLocation;
    className?: string;
    onlyMobile?: boolean;
  }
>(({ initLocation, className, onlyMobile }, ref) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { language } = useLanguage();

  const onError = () => {
    toast({
      title: 'Unable to retrieve your location, please search manually',
      variant: 'error',
    });
  };

  const readLocationFromBrowser = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(success, onError);
    } else {
      onError();
    }
  };

  const [location, setLocation] = useState<FoundLocation | undefined>(
    initLocation
  );

  const { originUrl } = useAppSettings();

  const router = useRouter();

  const onSubmit = (val: FoundLocation) => {
    setLocation(val);

    if (val) {
      const url = new URL(val.link, originUrl);
      url.searchParams.set('country', val.country);
      router.push(url.pathname + url.search);
    }
  };

  const success: PositionCallback = async (position) => {
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;

    const result = await systemRestClient.resolveCoordinates.query({
      query: { lng, lat, locale: language },
    });

    if (result.status === 200 && result.body !== null) {
      onSubmit(result.body);
    } else {
      onError();
    }
  };

  return isDesktop && onlyMobile !== true ? (
    <LocationSearchDesktop
      className={className}
      ref={ref}
      readLocationFromBrowser={readLocationFromBrowser}
      onSelect={onSubmit}
      language={language}
      location={location}
    ></LocationSearchDesktop>
  ) : (
    <LocationSearchMobile
      ref={ref}
      readLocationFromBrowser={readLocationFromBrowser}
      onSelect={onSubmit}
      language={language}
      className={className}
      location={location}
    ></LocationSearchMobile>
  );
});
LocationSearchHero.displayName = 'LocationSearch';

export default LocationSearchHero;
